import { createElement, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'next/link';

const Button = forwardRef(({
  as,
  className,
  disabled,
  href,
  size,
  variant,
  type,
  progress,
  linkProps,
  children,
  ...props
}, ref) => {
  if (disabled) {
    props.tabIndex = -1;
  }

  const buttonProps = {
    className: classNames(
      'button',
      `button--${size || 'md'}`,
      `button--${variant || 'primary'}`,
      disabled && 'button--disabled',
      typeof progress === 'number' && 'button--progress',
      className,
    ),
    type: as && as !== 'button' ? type : type || 'button',
    disabled: (!as || as === 'button') ? disabled : null,
    ref,
    ...props,
  };

  const buttonChildren = (
    <>
      {typeof progress === 'number' && (
        <span
          className={classNames(
            'button__progress',
            progress >= 0.5 && 'button__progress--lte-half',
          )}
          style={{
            transform: `scaleX(${1 - progress})`,
          }}
        />
      )}
      {!!children && (
        <span className="button__content">
          {children}
        </span>
      )}
    </>
  );

  if (as === Link) {
    return (
      <Link href={href} {...linkProps}>
        <a {...buttonProps}>
          {buttonChildren}
        </a>
      </Link>
    );
  }

  return createElement(as || 'button', {
    href,
    ...buttonProps,
  }, buttonChildren);
});

Button.displayName = 'Button';

Button.propTypes = {
  as: PropTypes.elementType,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  href: PropTypes.string,
  size: PropTypes.oneOf([
    'sm',
    'md',
    'lg',
  ]),
  variant: PropTypes.oneOf([
    'primary',
    'primary-ghost',
    'secondary',
  ]),
  type: PropTypes.oneOf([
    'button',
    'reset',
    'submit',
  ]),
  progress: PropTypes.number,
  linkProps: PropTypes.object,
  children: PropTypes.node,
};

export default Button;
